import { useEffect, useLayoutEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useNotifications } from '../../context/NotificationContext';
import { buildApiUrl, getApiBaseUrl } from '../../config/apiConfig';
import './MyEventDetailPage.css';
import { logError } from '../../utils/logger';

const resolveImageUrl = (imagePath) => {
  if (!imagePath) return null;
  const clean = String(imagePath).replace(/\\/g, '/').replace(/\s/g, '%20');
  if (/^https?:\/\//i.test(clean)) return clean;
  const base = getApiBaseUrl();
  if (clean.startsWith('/')) return `${base}${clean}`;
  if (clean.startsWith('uploads/')) return `${base}/${clean}`;
  return `${base}/uploads/${clean}`;
};

const formatDate = (dateString) => {
  if (!dateString) return '';
  const date = new Date(dateString);
  return date.toLocaleDateString('es-AR', {
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });
};

const formatTime = (timeString) => {
  if (!timeString) return '';
  // Si viene en formato HH:MM:SS, tomar solo HH:MM
  return timeString.substring(0, 5);
};

// Componente de calendario mini
const MiniCalendar = ({ eventDates, selectedDate, onDateSelect }) => {
  const [currentMonth, setCurrentMonth] = useState(new Date());
  
  // Obtener fechas del evento
  const dates = eventDates.map(d => {
    const dateStr = d.fecha || d;
    return new Date(dateStr + 'T00:00:00');
  }).filter(d => !isNaN(d.getTime()));
  
  const monthStart = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), 1);
  const monthEnd = new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 0);
  const startDate = new Date(monthStart);
  startDate.setDate(startDate.getDate() - startDate.getDay());
  
  const days = [];
  const day = new Date(startDate);
  
  while (day <= monthEnd || days.length < 42) {
    days.push(new Date(day));
    day.setDate(day.getDate() + 1);
    if (days.length >= 42) break;
  }
  
  const isEventDate = (date) => {
    return dates.some(d => 
      d.getFullYear() === date.getFullYear() &&
      d.getMonth() === date.getMonth() &&
      d.getDate() === date.getDate()
    );
  };
  
  const isSelected = (date) => {
    if (!selectedDate) return false;
    return date.getFullYear() === selectedDate.getFullYear() &&
           date.getMonth() === selectedDate.getMonth() &&
           date.getDate() === selectedDate.getDate();
  };
  
  const isCurrentMonth = (date) => {
    return date.getMonth() === currentMonth.getMonth();
  };
  
  const monthNames = ['Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio', 
                      'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'];
  const dayNames = ['Dom', 'Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb'];
  
  const prevMonth = () => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1, 1));
  };
  
  const nextMonth = () => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 1));
  };
  
  return (
    <div className="mini-calendar">
      <div className="mini-calendar-header">
        <button className="mini-calendar-nav" onClick={prevMonth}>
          <i className="fas fa-chevron-left" />
        </button>
        <h3 className="mini-calendar-month">
          {monthNames[currentMonth.getMonth()]} {currentMonth.getFullYear()}
        </h3>
        <button className="mini-calendar-nav" onClick={nextMonth}>
          <i className="fas fa-chevron-right" />
        </button>
      </div>
      
      <div className="mini-calendar-weekdays">
        {dayNames.map(day => (
          <div key={day} className="mini-calendar-weekday">{day}</div>
        ))}
      </div>
      
      <div className="mini-calendar-days">
        {days.map((date, idx) => {
          const eventDate = isEventDate(date);
          const selected = isSelected(date);
          const currentMonthDay = isCurrentMonth(date);
          
          return (
            <button
              key={idx}
              className={`mini-calendar-day ${!currentMonthDay ? 'other-month' : ''} ${eventDate ? 'has-event' : ''} ${selected ? 'selected' : ''}`}
              onClick={() => currentMonthDay && onDateSelect(date)}
              disabled={!currentMonthDay || !eventDate}
            >
              {date.getDate()}
            </button>
          );
        })}
      </div>
      
      <div className="mini-calendar-legend">
        <div className="legend-item">
          <span className="legend-dot has-event"></span>
          <span>Fechas del evento</span>
        </div>
      </div>
    </div>
  );
};

const MyEventDetailPage = () => {
  const { eventId } = useParams();
  const navigate = useNavigate();
  const { user, token, isAuthenticated } = useAuth();
  const { notify } = useNotifications();
  
  const [event, setEvent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedDay, setSelectedDay] = useState(null);

  const resolvedEventId = parseInt(String(eventId ?? ''), 10);

  useLayoutEffect(() => {
    setEvent(null);
    setSelectedDate(null);
    setSelectedDay(null);
    setError(null);
    setLoading(true);
    window.scrollTo(0, 0);
  }, [eventId]);
  
  useEffect(() => {
    if (!isAuthenticated || !user || !token) {
      navigate('/login', { 
        state: { 
          from: `/my-events/${eventId}`,
          message: 'Debes iniciar sesión para ver los detalles del evento'
        } 
      });
      return;
    }
    
    fetchEventDetail();
  }, [eventId, isAuthenticated, user, token, navigate]);
  
  const fetchEventDetail = async () => {
    if (!token) return;

    if (!Number.isFinite(resolvedEventId) || resolvedEventId <= 0) {
      setError('Identificador de evento no válido');
      setLoading(false);
      setEvent(null);
      return;
    }
    
    setLoading(true);
    setError(null);
    
    try {
      const response = await fetch(buildApiUrl(`/api/user/events/${resolvedEventId}`), {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });
      
      if (response.status === 401) {
        navigate('/login', { 
          state: { 
            from: `/my-events/${resolvedEventId}`,
            message: 'Tu sesión ha expirado'
          } 
        });
        return;
      }
      
      if (response.status === 403) {
        setError('No tienes acceso a este evento');
        return;
      }
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: 'Error desconocido' }));
        throw new Error(errorData.error || `Error al cargar evento (${response.status})`);
      }
      
      const data = await response.json();
      setEvent(data.event);
      
      // Seleccionar el primer día por defecto
      if (data.event.dias && data.event.dias.length > 0) {
        const firstDay = data.event.dias[0];
        const firstDate = new Date(firstDay.fecha + 'T00:00:00');
        setSelectedDate(firstDate);
        setSelectedDay(firstDay);
      }
    } catch (err) {
      logError('[MyEventDetailPage] Error cargando evento:', err);
      setError(err.message || 'Error al cargar los detalles del evento');
      notify({
        message: err.message || 'Error al cargar los detalles del evento',
        type: 'error',
      });
    } finally {
      setLoading(false);
    }
  };
  
  const handleDateSelect = (date) => {
    setSelectedDate(date);
    // Buscar el día correspondiente
    const day = event.dias.find(d => {
      const dayDate = new Date(d.fecha + 'T00:00:00');
      return dayDate.getFullYear() === date.getFullYear() &&
             dayDate.getMonth() === date.getMonth() &&
             dayDate.getDate() === date.getDate();
    });
    setSelectedDay(day || null);
  };
  
  const copyToClipboard = (text, label) => {
    navigator.clipboard.writeText(text).then(() => {
      notify({
        message: `${label} copiado al portapapeles`,
        type: 'success',
      });
    }).catch(() => {
      notify({
        message: `Error al copiar ${label}`,
        type: 'error',
      });
    });
  };
  
  if (loading) {
    return (
      <div className="my-event-detail-page">
        <div className="my-event-detail-container">
          <div className="my-event-detail-loading">
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Cargando...</span>
            </div>
            <p>Cargando detalles del evento...</p>
          </div>
        </div>
      </div>
    );
  }
  
  if (error || !event) {
    return (
      <div className="my-event-detail-page">
        <div className="my-event-detail-container">
          <div className="my-event-detail-error">
            <i className="fas fa-exclamation-triangle" />
            <h2>Error al cargar evento</h2>
            <p>{error || 'Evento no encontrado'}</p>
            <button 
              className="btn btn-primary"
              onClick={() => navigate('/my-events')}
            >
              Volver a Mis Eventos
            </button>
          </div>
        </div>
      </div>
    );
  }
  
  const imageUrl = resolveImageUrl(event.imagen);
  const isVirtual = event.formato === 'virtual';
  const isPresencial = event.formato === 'presencial';
  const eventDates = event.dias || event.diaseventos || [];
  
  return (
    <div className="my-event-detail-page">
      <div className="my-event-detail-container">
        {/* Header con botón volver */}
        <div className="my-event-detail-header">
          <button 
            className="btn-back"
            onClick={() => navigate('/my-events')}
          >
            <i className="fas fa-arrow-left" />
            Volver a Mis Eventos
          </button>
        </div>
        
        {/* Hero Section */}
        <div className="my-event-detail-hero">
          {imageUrl && (
            <div className="my-event-detail-hero-image">
              <img src={imageUrl} alt={event.titulo} />
              <div className="my-event-detail-hero-overlay">
                <div className="my-event-detail-badge">
                  {isVirtual ? (
                    <span className="badge-virtual">
                      <i className="fas fa-video" /> Virtual
                    </span>
                  ) : (
                    <span className="badge-presencial">
                      <i className="fas fa-map-marker-alt" /> Presencial
                    </span>
                  )}
                </div>
              </div>
            </div>
          )}
          <div className="my-event-detail-hero-content">
            <h1 className="my-event-detail-title">{event.titulo}</h1>
            {event.descripcionBreve && (
              <p className="my-event-detail-subtitle">{event.descripcionBreve}</p>
            )}
            {event.acquiredAt && (
              <div className="my-event-detail-meta">
                <i className="fas fa-calendar-check" />
                <span>Adquirido: {formatDate(event.acquiredAt)}</span>
              </div>
            )}
          </div>
        </div>
        
        {/* Grid principal: Calendario y Días */}
        <div className="my-event-detail-main-grid">
          {/* Columna izquierda: Calendario */}
          <div className="my-event-detail-calendar-section">
            <h2 className="section-title">
              <i className="fas fa-calendar-alt" />
              Calendario del Evento
            </h2>
            <MiniCalendar 
              eventDates={eventDates}
              selectedDate={selectedDate}
              onDateSelect={handleDateSelect}
            />
          </div>
          
          {/* Columna derecha: Días del evento */}
          <div className="my-event-detail-days-section">
            <h2 className="section-title">
              <i className="fas fa-calendar-day" />
              Días del Evento
            </h2>
            {eventDates.length > 0 ? (
              <div className="event-days-list">
                {eventDates.map((day, idx) => {
                  const dayDate = new Date((day.fecha || day) + 'T00:00:00');
                  const isSelected = selectedDay && (
                    (selectedDay.fecha || selectedDay) === (day.fecha || day)
                  );
                  
                  return (
                    <div 
                      key={idx}
                      className={`event-day-card ${isSelected ? 'selected' : ''}`}
                      onClick={() => {
                        setSelectedDate(dayDate);
                        setSelectedDay(day);
                      }}
                    >
                      <div className="event-day-header">
                        <div className="event-day-date">
                          <i className="fas fa-calendar" />
                          <div>
                            <strong>{formatDate(day.fecha || day)}</strong>
                            {(day.horaInicio || day.horaInicio) && (
                              <span className="event-day-time">
                                {formatTime(day.horaInicio || day.horaInicio)}
                                {day.horaFin && ` - ${formatTime(day.horaFin)}`}
                              </span>
                            )}
                          </div>
                        </div>
                        {isSelected && (
                          <i className="fas fa-check-circle selected-indicator" />
                        )}
                      </div>
                      {day.titulo && (
                        <h3 className="event-day-title">{day.titulo}</h3>
                      )}
                      {day.descripcion && (
                        <p className="event-day-description">{day.descripcion}</p>
                      )}
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="empty-state">
                <i className="fas fa-calendar-times" />
                <p>No hay días programados para este evento</p>
              </div>
            )}
          </div>
        </div>
        
        {/* Información de acceso */}
        <div className="my-event-detail-access-section">
          <h2 className="section-title">
            <i className="fas fa-key" />
            Información de Acceso
          </h2>
          
          <div className="access-info-grid">
            {/* Código para eventos presenciales */}
            {isPresencial && event.codigo && (
              <div className="access-info-card">
                <div className="access-info-header">
                  <label>
                    <i className="fas fa-ticket-alt" />
                    Código de Acceso
                  </label>
                  <button
                    className="btn-copy"
                    onClick={() => copyToClipboard(event.codigo.code, 'Código')}
                    title="Copiar código"
                  >
                    <i className="fas fa-copy" />
                  </button>
                </div>
                <div className="access-info-value">
                  <code>{event.codigo.code}</code>
                  {event.codigo.status === 'used' && (
                    <span className="status-badge used">
                      <i className="fas fa-check-circle" /> Usado
                    </span>
                  )}
                </div>
                {event.codigo.assignedAt && (
                  <small className="access-info-meta">
                    Asignado: {formatDate(event.codigo.assignedAt)}
                  </small>
                )}
              </div>
            )}
            
            {/* Link para eventos virtuales */}
            {isVirtual && event.linkVirtual && (
              <div className="access-info-card">
                <div className="access-info-header">
                  <label>
                    <i className="fas fa-link" />
                    Link de Acceso Virtual
                  </label>
                  <button
                    className="btn-copy"
                    onClick={() => copyToClipboard(event.linkVirtual, 'Link')}
                    title="Copiar link"
                  >
                    <i className="fas fa-copy" />
                  </button>
                </div>
                <a
                  href={event.linkVirtual}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="access-info-link"
                >
                  <i className="fas fa-external-link-alt" />
                  Acceder al Evento Virtual
                </a>
              </div>
            )}
            
            {/* Dirección para eventos presenciales */}
            {isPresencial && event.direccion && (
              <div className="access-info-card">
                <div className="access-info-header">
                  <label>
                    <i className="fas fa-map-marker-alt" />
                    Dirección
                  </label>
                </div>
                <p className="access-info-address">{event.direccion}</p>
                {event.linkPresencial && (
                  <a
                    href={event.linkPresencial}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="btn btn-outline-primary btn-sm"
                  >
                    <i className="fas fa-map" />
                    Ver en Google Maps
                  </a>
                )}
              </div>
            )}
          </div>
        </div>
        
        {/* Clases del evento */}
        {event.clases && event.clases.length > 0 && (
          <div className="my-event-detail-classes-section">
            <h2 className="section-title">
              <i className="fas fa-chalkboard-teacher" />
              Clases del Evento
            </h2>
            <div className="event-classes-grid">
              {event.clases.map((clase, idx) => (
                <div key={idx} className="event-class-card">
                  <div className="event-class-header">
                    <h3 className="event-class-title">
                      <i className="fas fa-play-circle" />
                      {clase.tituloclaseevento || `Clase ${idx + 1}`}
                    </h3>
                    {clase.diaclase && (
                      <span className="event-class-day">{clase.diaclase}</span>
                    )}
                  </div>
                  {clase.descripcionbreve && (
                    <p className="event-class-description">{clase.descripcionbreve}</p>
                  )}
                  {clase.descripcioncompleta && (
                    <p className="event-class-full-description">{clase.descripcioncompleta}</p>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}
        
        {/* Descripción completa */}
        {event.descripcionCompleta && (
          <div className="my-event-detail-description-section">
            <h2 className="section-title">
              <i className="fas fa-info-circle" />
              Descripción Completa
            </h2>
            <div className="description-content">
              <p>{event.descripcionCompleta}</p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default MyEventDetailPage;

